import React, { useState, useEffect } from 'react';
import { User } from '@restore/services-lib';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import SingleSelectDropdown from '../SingleSelectDropdown';
import { UsersContextProvider, useUsersContext } from 'context/users.context';
import { getUsersName } from 'utils/formatting';

interface UserSingleSelectDropdownInterface {
  selectedUser?: User;
  selectedUserId?: number;
  onChange: (selectedOption?: DropdownOption) => void;
  label?: string;
  disabled?: boolean;
}

const getSelectedUser = (user?: User) => user && { id: user.id, name: getUsersName(user) };
const getSelectedUserFromId = (
  availableUsers: User[],
  userId?: number
): DropdownOption | undefined => {
  const user = userId ? availableUsers.filter((u) => u.id === userId)[0] : undefined;
  return user ? { id: user.id, name: getUsersName(user) } : undefined;
};

const UserSingleSelectDropdownComponent = ({
  selectedUser,
  selectedUserId,
  onChange,
  label,
  disabled,
}: UserSingleSelectDropdownInterface) => {
  const { users: availableUsers } = useUsersContext();
  const [selectedOption, setSelectedOption] = useState<DropdownOption | undefined>(
    selectedUser
      ? getSelectedUser(selectedUser)
      : getSelectedUserFromId(availableUsers, selectedUserId)
  );

  useEffect(() => {
    setSelectedOption(
      selectedUser
        ? getSelectedUser(selectedUser)
        : getSelectedUserFromId(availableUsers, selectedUserId)
    );
  }, [selectedUser, selectedUserId, availableUsers]);

  const usersOptions: DropdownOption[] = availableUsers.map((user) => ({
    name: getUsersName(user),
    id: user.id,
  }));

  const handleChange = (newSelectedOption?: DropdownOption) => {
    setSelectedOption(newSelectedOption);
    onChange(newSelectedOption);
  };

  return (
    <SingleSelectDropdown
      disabled={disabled}
      fieldLabel={label ?? 'user'}
      allOptions={usersOptions}
      fullWidth={true}
      selected={selectedOption}
      setSelected={handleChange}
      variant='outlined'
    />
  );
};

const UserSingleSelectDropdown = ({
  selectedUser,
  selectedUserId,
  onChange,
  label,
  disabled,
}: UserSingleSelectDropdownInterface) => (
  <UsersContextProvider>
    <UserSingleSelectDropdownComponent
      selectedUserId={selectedUserId}
      selectedUser={selectedUser}
      onChange={onChange}
      label={label}
      disabled={disabled}
    />
  </UsersContextProvider>
);

export default UserSingleSelectDropdown;
